import { useCallback, useEffect, useRef, useState } from "react";
import * as spacesApi from "@/api/spaces";
import {
  useCursorInputContext,
  type CursorInputContextValue,
} from "@/components/layout/cursor-input-context";
import { currentFrameCenter } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { translate, useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { useFramesStore } from "@/stores/frames-store";
import { useTasksStore } from "@/stores/tasks-store";
import { postComment } from "@/api/tasks";
import { isTransientConversationFrameMetadata } from "@/lib/transient-conversation-frame";

const FRAME_REPLY_ROUTE_CACHE_MAX = 64;
const FRAME_REPLY_ROUTE_CACHE_TTL_MS = 10_000;
const frameReplyRouteCache = new Map<
  string,
  {
    expiresAt: number;
    request: ReturnType<typeof spacesApi.getFrameReplyRoute>;
  }
>();

function cachedFrameReplyRoute(
  projectId: string,
  spaceId: string,
  frameId: string,
) {
  const key = `${projectId}:${spaceId}:${frameId}`;
  const cached = frameReplyRouteCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.request;
  if (cached) frameReplyRouteCache.delete(key);
  const request = spacesApi
    .getFrameReplyRoute(projectId, spaceId, frameId)
    .then((route) => {
      if (!route.conversation_id) frameReplyRouteCache.delete(key);
      return route;
    })
    .catch((error) => {
      if (frameReplyRouteCache.get(key)?.request === request) {
        frameReplyRouteCache.delete(key);
      }
      throw error;
    });
  if (frameReplyRouteCache.size >= FRAME_REPLY_ROUTE_CACHE_MAX) {
    const oldestKey = frameReplyRouteCache.keys().next().value;
    if (oldestKey) frameReplyRouteCache.delete(oldestKey);
  }
  frameReplyRouteCache.set(key, {
    expiresAt: Date.now() + FRAME_REPLY_ROUTE_CACHE_TTL_MS,
    request,
  });
  return request;
}

function metadataString(
  metadata: Record<string, unknown> | undefined,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = metadata?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function showReplyError(description: string): void {
  useNotificationStore.getState().addSystemToast({
    id: `frame-reply-${Date.now()}`,
    level: "error",
    title: translate(framesSharedMessages, "replyFailed"),
    description,
  });
}

interface FrameReplyOptions {
  conversationId?: string | null;
  agentKey?: string | null;
  transientConversation?: boolean;
  inputContext?: CursorInputContextValue | null;
  requireExistingConversation?: boolean;
}

export function useFrameReply(frameId: string, options?: FrameReplyOptions) {
  const t = useT(framesSharedMessages);
  const inheritedInput = useCursorInputContext();
  const cursorInput = options?.inputContext ?? inheritedInput;
  const hintedConversationId = options?.conversationId;
  const hintedAgentKey = options?.agentKey;
  const transientConversation = options?.transientConversation === true;
  const requireExistingConversation =
    options?.requireExistingConversation === true;
  const currentProjectId = useProjectStore((state) => state.currentProject?.id);
  const routePromiseRef = useRef<ReturnType<
    typeof spacesApi.getFrameReplyRoute
  > | null>(null);
  const sendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const [resolvedRoute, setResolvedRoute] =
    useState<spacesApi.FrameReplyRoute | null>(null);

  const resolveRoute = useCallback(() => {
    if (routePromiseRef.current) return routePromiseRef.current;
    if (transientConversation && hintedConversationId) {
      const hintedRoute = Promise.resolve<spacesApi.FrameReplyRoute>({
        conversation_id: hintedConversationId,
        agent_key: hintedAgentKey ?? null,
        task_id: null,
        context_kind: null,
        source: "frame_metadata",
      });
      routePromiseRef.current = hintedRoute;
      return hintedRoute;
    }
    const frame = useFramesStore.getState().framesById[frameId];
    const conversationId = frame
      ? metadataString(frame.metadata, "conversationId", "conversation_id")
      : null;
    if (
      frame &&
      isTransientConversationFrameMetadata(frame.metadata) &&
      conversationId
    ) {
      const directRoute = Promise.resolve<spacesApi.FrameReplyRoute>({
        conversation_id: conversationId,
        agent_key: metadataString(frame.metadata, "agentKey", "agent_key"),
        task_id: null,
        context_kind: null,
        source: "frame_metadata",
      });
      routePromiseRef.current = directRoute;
      return directRoute;
    }
    const { currentProject, currentSpace } = useProjectStore.getState();
    if (!currentProject || !currentSpace) return null;
    const request = cachedFrameReplyRoute(
      currentProject.id,
      currentSpace.id,
      frameId,
    )
      .then((route) => {
        if (!route.conversation_id && routePromiseRef.current === request) {
          routePromiseRef.current = null;
        }
        return route;
      })
      .catch((error) => {
        routePromiseRef.current = null;
        throw error;
      });
    routePromiseRef.current = request;
    return request;
  }, [frameId, hintedAgentKey, hintedConversationId, transientConversation]);

  useEffect(() => {
    let active = true;
    void resolveRoute()
      ?.then((route) => {
        if (active) setResolvedRoute(route);
      })
      .catch(() => {
        if (active) setResolvedRoute(null);
      });
    return () => {
      active = false;
    };
  }, [resolveRoute]);

  const send = useCallback(
    async (rawText: string): Promise<boolean> => {
      const text = rawText.trim();
      if (!text || sendingRef.current) return false;

      const routeRequest = resolveRoute();
      if (!routeRequest) {
        showReplyError(t("replyOpenInProjectSpace"));
        return false;
      }

      sendingRef.current = true;
      setIsSending(true);
      try {
        const route = await routeRequest;
        setResolvedRoute(route);
        if (requireExistingConversation && !route.conversation_id) {
          showReplyError(t("replyNotLinked"));
          return false;
        }
        if (requireExistingConversation && route.native_child) {
          if (!cursorInput) {
            showReplyError(t("replyOpenWorkspaceChat"));
            return false;
          }
          const result = await cursorInput.onSubmit(text, "denker", {
            conversationId: route.conversation_id!,
            composerId: `frame-followup:${frameId}`,
            ignoreInputBarSessionState: true,
            nativeChildInstruction: true,
            textOnly: true,
            position: currentFrameCenter(frameId),
          });
          return result !== false;
        }
        if (route.task_id && !requireExistingConversation) {
          if (!currentProjectId) {
            showReplyError(t("replyOpenInProject"));
            return false;
          }
          await postComment(
            currentProjectId,
            route.task_id,
            text,
            null,
            undefined,
            frameId,
          );
          useTasksStore.getState().triggerRefetch();
          return true;
        }
        if (!cursorInput) {
          showReplyError(t("replyOpenWorkspaceChat"));
          return false;
        }
        const frame = useFramesStore.getState().framesById[frameId];
        const mentionedFrames =
          transientConversation ||
          isTransientConversationFrameMetadata(frame?.metadata)
            ? undefined
            : [
                {
                  frame_id: frameId,
                  title: frame?.title || "Untitled",
                  frame_type: frame?.frameType || "unknown",
                },
              ];
        if (!route.conversation_id) {
          const result = await cursorInput.onSubmit(
            text,
            route.agent_key ?? undefined,
            {
              composerId: `frame-followup:${frameId}`,
              ignoreInputBarSessionState: true,
              position: currentFrameCenter(frameId),
              startNewConversation: true,
              mentionedFrames,
            },
          );
          return result !== false;
        }
        if (!route.agent_key) {
          showReplyError(t("replyAgentUnresolved"));
          return false;
        }
        if (
          requireExistingConversation &&
          route.agent_key.toLowerCase() !== "denker"
        ) {
          showReplyError(t("replyAgentUnavailable"));
          return false;
        }
        const result = await cursorInput.onSubmit(text, route.agent_key, {
          conversationId: route.conversation_id,
          keepConversation: true,
          mentionedFrames,
          composerId: `frame-followup:${frameId}`,
          ignoreInputBarSessionState: true,
          ...(requireExistingConversation ? { textOnly: true } : {}),
          position: currentFrameCenter(frameId),
        });
        return result !== false;
      } catch (error) {
        showReplyError(
          error instanceof Error ? error.message : t("pleaseTryAgain"),
        );
        return false;
      } finally {
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [
      currentProjectId,
      cursorInput,
      frameId,
      resolveRoute,
      requireExistingConversation,
      t,
      transientConversation,
    ],
  );

  return {
    canSend: requireExistingConversation
      ? Boolean(
          resolvedRoute?.conversation_id &&
          cursorInput !== null &&
          (resolvedRoute.native_child ||
            resolvedRoute.agent_key?.toLowerCase() === "denker"),
        )
      : cursorInput !== null || resolvedRoute?.task_id != null,
    unavailableReason:
      requireExistingConversation &&
      resolvedRoute?.conversation_id &&
      !resolvedRoute.native_child &&
      resolvedRoute.agent_key?.toLowerCase() !== "denker"
        ? t("replyAgentUnavailable")
        : t("replyConversationUnavailable"),
    isSending,
    send,
  };
}
